'use client'
import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react'
// Note: All inventory operations are handled via API calls to prevent client-side Prisma usage

// Types for bulletproof cart system
export interface CartItem {
  id: string
  cartId: string
  eventId: string
  ticketTypeId: string
  quantity: number
  priceAtTime: number
  createdAt: Date
  updatedAt: Date
  event: {
    id: string
    name: string
    slug: string
    startDate: Date
    venue: {
      name: string
      city: string
      state: string
    }
  }
  ticketType: {
    id: string
    name: string
    description?: string
    price: number
    quantity: number
    sold: number
    reserved: number
    minQuantity: number
    maxQuantity: number
    quantityInterval: number
    isVisible: boolean
    category: string
  }
}

export interface Cart {
  id: string
  sessionId: string
  userId: string | null
  clientId: string
  items: CartItem[]
  promoCode: string | null
  discount: number
  expiresAt: Date
  createdAt: Date
  updatedAt: Date
}

export interface CartSummary {
  itemCount: number
  subtotal: number
  fees: number
  tax: number
  discount: number
  total: number
}

export interface CartError {
  ticketTypeId: string
  error: string
  timestamp: number
  suggested?: number
  conflictDetails?: {
    requested: number
    actualAvailable: number
    heldByOthers: number
    sold: number
    total: number
  }
}

export interface CartState {
  cart: Cart | null
  loading: boolean
  errors: Record<string, CartError>
  pendingOperations: Record<string, boolean>
  lastSync: number
  sessionId: string
}

export interface CartActions {
  // Core operations with bulletproof validation
  addItem: (eventId: string, ticketTypeId: string, quantity: number) => Promise<{ success: boolean; error?: string }>
  updateQuantity: (ticketTypeId: string, quantity: number) => Promise<{ success: boolean; error?: string }>
  removeItem: (ticketTypeId: string) => Promise<{ success: boolean; error?: string }>
  
  // Optimistic operations with rollback
  incrementTicket: (ticketTypeId: string, eventId: string) => void
  decrementTicket: (ticketTypeId: string) => void
  
  // Real-time validation
  canAddTickets: (ticketTypeId: string, quantity: number) => Promise<{ canAdd: boolean; available: number; error?: string }>
  getAvailableQuantity: (ticketTypeId: string) => Promise<number>
  
  // Cart management
  refreshCart: () => Promise<void>
  clearCart: () => Promise<void>
  initializeCart: (sessionId?: string) => void
  
  // Promo code management
  applyPromoCode: (promoCode: string) => Promise<{ success: boolean; error?: string; message?: string }>
  removePromoCode: () => Promise<{ success: boolean; error?: string }>
  
  // Error handling
  clearError: (ticketTypeId: string) => void
  getError: (ticketTypeId: string) => CartError | null
  
  // State queries
  getCartQuantity: (ticketTypeId: string) => number
  isUpdating: (ticketTypeId: string) => boolean
  getSummary: () => CartSummary
}

const CartContext = createContext<(CartState & CartActions) | null>(null)

// Generate session ID
function generateSessionId(): string {
  return `cart_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
}

export function BulletproofCartProvider({ children }: { children: React.ReactNode }) {
  // Core state
  const [cart, setCart] = useState<Cart | null>(null)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, CartError>>({})
  const [pendingOperations, setPendingOperations] = useState<Record<string, boolean>>({})
  const [sessionId, setSessionId] = useState<string>('')
  const [lastSync, setLastSync] = useState<number>(0)
  
  // Optimistic updates for immediate UI feedback
  const [optimisticQuantities, setOptimisticQuantities] = useState<Record<string, number>>({})
  
  // Cleanup and sync timers
  const cleanupTimerRef = useRef<NodeJS.Timeout | null>(null)
  const syncTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Initialize session (SSR-safe)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      let storedSessionId = localStorage.getItem('cart_session_id')
      if (!storedSessionId) {
        storedSessionId = generateSessionId()
        localStorage.setItem('cart_session_id', storedSessionId)
        if (process.env.NODE_ENV !== 'production') console.log('Created new cart session:', storedSessionId)
      } else {
        if (process.env.NODE_ENV !== 'production') console.log('Using existing cart session:', storedSessionId)
      }
      setSessionId(storedSessionId)
    }
  }, [])

  // Clear cart on page unload if expired
  useEffect(() => {
    const handlePageUnload = () => {
      if (cart && cart.expiresAt && new Date() > new Date(cart.expiresAt)) {
        localStorage.removeItem('cart_session_id')
      }
    }

    window.addEventListener('beforeunload', handlePageUnload)
    window.addEventListener('pagehide', handlePageUnload)
    
    return () => {
      window.removeEventListener('beforeunload', handlePageUnload)
      window.removeEventListener('pagehide', handlePageUnload)
    }
  }, [cart])

  // Note: Cleanup of expired holds is handled server-side via API routes

  // Fetch cart from server
  const refreshCart = useCallback(async () => {
    if (!sessionId) return

    try {
      setLoading(true)
      const isDev = process.env.NODE_ENV !== 'production'
      if (isDev) console.log('Refreshing cart for session:', sessionId)
      const response = await fetch(`/api/cart?sessionId=${sessionId}`)
      const data = await response.json()
      if (isDev) {
        console.log('Cart API response:', {
          success: data.success,
          hasData: !!data.data,
          itemCount: data.data?.items?.length || 0,
          sessionId: sessionId
        })
      }

      if (data.success && data.data) {
        // Check if cart is expired
        const cartData = data.data
        if (cartData.expiresAt && new Date() > new Date(cartData.expiresAt)) {
          console.log('Cart expired, clearing session')
          localStorage.removeItem('cart_session_id')
          const newSessionId = generateSessionId()
          localStorage.setItem('cart_session_id', newSessionId)
          setSessionId(newSessionId)
          setCart(null)
          setLastSync(Date.now())
          return
        }

        console.log('Setting cart data with', cartData.items?.length || 0, 'items')
        setCart(cartData)
        setLastSync(Date.now())

        // Clear optimistic quantities when real data arrives
        setOptimisticQuantities({})
      } else {
        // No cart data is the expected state for empty/new sessions —
        // demote to debug log so prod console isn't spammed.
        if (process.env.NODE_ENV !== 'production') console.log('No cart data received:', data)
        setLastSync(Date.now()) // Mark as synced even if no data
      }
    } catch (error) {
      console.error('Failed to refresh cart:', error)
      setLastSync(Date.now()) // Mark as synced to prevent infinite loading
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  // Add error with automatic cleanup
  const addError = useCallback((ticketTypeId: string, error: string, suggested?: number, conflictDetails?: any) => {
    const errorObj: CartError = {
      ticketTypeId,
      error,
      timestamp: Date.now(),
      suggested,
      conflictDetails
    }
    
    setErrors(prev => ({ ...prev, [ticketTypeId]: errorObj }))
    
    // Auto-clear error after 5 seconds
    setTimeout(() => {
      setErrors(prev => {
        const { [ticketTypeId]: _, ...rest } = prev
        return rest
      })
    }, 5000)
  }, [])

  // Clear specific error
  const clearError = useCallback((ticketTypeId: string) => {
    setErrors(prev => {
      const { [ticketTypeId]: _, ...rest } = prev
      return rest
    })
  }, [])

  // Get error for ticket type
  const getError = useCallback((ticketTypeId: string): CartError | null => {
    return errors[ticketTypeId] || null
  }, [errors])

  // Get cart quantity (optimistic or actual)
  const getCartQuantity = useCallback((ticketTypeId: string): number => {
    // Return optimistic quantity if available
    if (ticketTypeId in optimisticQuantities) {
      return optimisticQuantities[ticketTypeId]
    }
    
    // Fall back to actual cart data
    const cartItem = cart?.items?.find(item => item.ticketTypeId === ticketTypeId)
    return cartItem?.quantity || 0
  }, [cart, optimisticQuantities])

  // Check if ticket type is being updated
  const isUpdating = useCallback((ticketTypeId: string): boolean => {
    return pendingOperations[ticketTypeId] || false
  }, [pendingOperations])

  // Real-time availability check via API (with basic caching)
  const canAddTickets = useCallback(async (ticketTypeId: string, quantity: number): Promise<{ canAdd: boolean; available: number; error?: string }> => {
    try {
      const response = await fetch(`/api/ticket-types/${ticketTypeId}`)
      const data = await response.json()
      
      if (data.success && data.data) {
        const available = data.data.availableQuantity
        return {
          canAdd: quantity <= available,
          available,
          error: quantity > available ? `Only ${available} tickets available` : undefined
        }
      }
      
      return {
        canAdd: false,
        available: 0,
        error: 'Failed to check availability'
      }
    } catch (error) {
      return {
        canAdd: false,
        available: 0,
        error: error instanceof Error ? error.message : 'Validation failed'
      }
    }
  }, [])

  // Get available quantity for ticket type via API
  const getAvailableQuantity = useCallback(async (ticketTypeId: string): Promise<number> => {
    try {
      const response = await fetch(`/api/ticket-types/${ticketTypeId}`)
      const data = await response.json()
      
      if (data.success && data.data) {
        return data.data.availableQuantity
      }
      
      return 0
    } catch (error) {
      console.error('Failed to get availability:', error)
      return 0
    }
  }, [])

  // Bulletproof add item via API
  const addItem = useCallback(async (eventId: string, ticketTypeId: string, quantity: number): Promise<{ success: boolean; error?: string }> => {
    if (!sessionId) {
      return { success: false, error: 'Session not initialized' }
    }

    setPendingOperations(prev => ({ ...prev, [ticketTypeId]: true }))
    clearError(ticketTypeId)

    try {
      // Use the bulletproof API route
      const response = await fetch('/api/cart/items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId,
          ticketTypeId,
          quantity,
          sessionId
        })
      })

      const result = await response.json()

      if (result.success) {
        // Update local cart state
        setCart(result.data)
        return { success: true }
      } else {
        addError(ticketTypeId, result.error || 'Failed to add item')
        return { success: false, error: result.error }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add item'
      addError(ticketTypeId, errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setPendingOperations(prev => ({ ...prev, [ticketTypeId]: false }))
    }
  }, [sessionId, addError, clearError])

  // Bulletproof update quantity
  const updateQuantity = useCallback(async (ticketTypeId: string, quantity: number): Promise<{ success: boolean; error?: string }> => {
    if (!sessionId) {
      return { success: false, error: 'Session not initialized' }
    }

    const cartItem = cart?.items?.find(item => item.ticketTypeId === ticketTypeId)
    if (!cartItem) {
      return { success: false, error: 'Item not found in cart' }
    }

    setPendingOperations(prev => ({ ...prev, [ticketTypeId]: true }))
    clearError(ticketTypeId)

    try {
      // Use the bulletproof API route for updates
      const response = await fetch(`/api/cart/items/${cartItem.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity,
          sessionId
        })
      })

      const result = await response.json()

      if (result.success) {
        // Update local cart state
        setCart(result.data)
        return { success: true }
      } else {
        addError(ticketTypeId, result.error || 'Failed to update item')
        return { success: false, error: result.error }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update item'
      addError(ticketTypeId, errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setPendingOperations(prev => ({ ...prev, [ticketTypeId]: false }))
    }
  }, [sessionId, cart, addError, clearError])

  // Remove item
  const removeItem = useCallback(async (ticketTypeId: string): Promise<{ success: boolean; error?: string }> => {
    return updateQuantity(ticketTypeId, 0)
  }, [updateQuantity])

  // Optimistic increment with rollback
  const incrementTicket = useCallback((ticketTypeId: string, eventId: string) => {
    const currentQty = getCartQuantity(ticketTypeId)
    const newQty = currentQty + 1
    
    // Optimistically update UI
    setOptimisticQuantities(prev => ({ ...prev, [ticketTypeId]: newQty }))
    clearError(ticketTypeId)
    
    // Debounced sync
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current)
    }
    
    syncTimerRef.current = setTimeout(async () => {
      const result = currentQty === 0 
        ? await addItem(eventId, ticketTypeId, newQty)
        : await updateQuantity(ticketTypeId, newQty)
      
      if (!result.success) {
        // Rollback optimistic update
        setOptimisticQuantities(prev => ({ ...prev, [ticketTypeId]: currentQty }))
      }
    }, 500)
  }, [getCartQuantity, addItem, updateQuantity, clearError])

  // Optimistic decrement
  const decrementTicket = useCallback((ticketTypeId: string) => {
    const currentQty = getCartQuantity(ticketTypeId)
    const newQty = Math.max(0, currentQty - 1)
    
    // Optimistically update UI
    setOptimisticQuantities(prev => ({ ...prev, [ticketTypeId]: newQty }))
    clearError(ticketTypeId)
    
    // Debounced sync
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current)
    }
    
    syncTimerRef.current = setTimeout(async () => {
      const result = await updateQuantity(ticketTypeId, newQty)
      
      if (!result.success) {
        // Rollback optimistic update
        setOptimisticQuantities(prev => ({ ...prev, [ticketTypeId]: currentQty }))
      }
    }, 500)
  }, [getCartQuantity, updateQuantity, clearError])

  // Clear cart
  const clearCart = useCallback(async () => {
    if (!sessionId) return

    try {
      const response = await fetch(`/api/cart?sessionId=${sessionId}`, {
        method: 'DELETE'
      })
      
      if (response.ok) {
        setCart(null)
        setOptimisticQuantities({})
        setErrors({})
        
        // Generate new session
        const newSessionId = generateSessionId()
        localStorage.setItem('cart_session_id', newSessionId)
        setSessionId(newSessionId)
      }
    } catch (error) {
      console.error('Failed to clear cart:', error)
    }
  }, [sessionId])

  // Calculate cart summary
  const getSummary = useCallback((): CartSummary => {
    if (!cart?.items) {
      return { itemCount: 0, subtotal: 0, fees: 0, tax: 0, discount: 0, total: 0 }
    }

    const itemCount = cart.items.reduce((total, item) => {
      const quantity = getCartQuantity(item.ticketTypeId)
      return total + quantity
    }, 0)

    const subtotal = cart.items.reduce((total, item) => {
      const quantity = getCartQuantity(item.ticketTypeId)
      return total + (item.priceAtTime * quantity)
    }, 0)

    const fees = subtotal * 0.05 + 2.50 // 5% platform fee + $2.50 flat fee
    const tax = subtotal * 0.08 // 8% tax on subtotal only
    const discount = cart.discount || 0
    const total = subtotal + fees + tax - discount

    return { itemCount, subtotal, fees, tax, discount, total }
  }, [cart, getCartQuantity])

  // Apply promo code
  const applyPromoCode = useCallback(async (promoCode: string) => {
    if (!sessionId || !promoCode.trim()) {
      return { success: false, error: 'Session ID and promo code are required' }
    }

    try {
      setLoading(true)
      const response = await fetch('/api/cart/promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, promoCode: promoCode.trim() })
      })

      const data = await response.json()

      if (data.success && data.cart) {
        setCart(data.cart)
        setLastSync(Date.now())
        return { success: true, message: data.message }
      } else {
        return { success: false, error: data.error || 'Failed to apply promo code' }
      }
    } catch (error) {
      console.error('Apply promo code error:', error)
      return { success: false, error: 'Network error applying promo code' }
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  // Remove promo code
  const removePromoCode = useCallback(async () => {
    if (!sessionId) {
      return { success: false, error: 'Session ID is required' }
    }

    try {
      setLoading(true)
      const response = await fetch('/api/cart/promo', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      })

      const data = await response.json()

      if (data.success && data.cart) {
        setCart(data.cart)
        setLastSync(Date.now())
        return { success: true }
      } else {
        return { success: false, error: data.error || 'Failed to remove promo code' }
      }
    } catch (error) {
      console.error('Remove promo code error:', error)
      return { success: false, error: 'Network error removing promo code' }
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  // Load cart when session ID is available
  useEffect(() => {
    if (sessionId) {
      refreshCart()
    }
  }, [sessionId, refreshCart])

  // Initialize cart when session is ready
  const initializeCart = useCallback((newSessionId?: string) => {
    if (newSessionId) {
      setSessionId(newSessionId)
      localStorage.setItem('cart_session_id', newSessionId)
    }
    if (sessionId) {
      refreshCart()
    }
  }, [sessionId, refreshCart])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupTimerRef.current) {
        clearInterval(cleanupTimerRef.current)
      }
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current)
      }
    }
  }, [])

  const contextValue: CartState & CartActions = {
    // State
    cart,
    loading,
    errors,
    pendingOperations,
    lastSync,
    sessionId,
    
    // Actions
    addItem,
    updateQuantity,
    removeItem,
    incrementTicket,
    decrementTicket,
    canAddTickets,
    getAvailableQuantity,
    refreshCart,
    clearCart,
    initializeCart,
    clearError,
    getError,
    getCartQuantity,
    isUpdating,
    getSummary,
    applyPromoCode,
    removePromoCode
  }

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  )
}

// Hook to use the bulletproof cart
export function useBulletproofCart() {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useBulletproofCart must be used within a BulletproofCartProvider')
  }
  return context
}